import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { MapPin, Mail, Phone, Clock } from 'lucide-react';
import { isArchitectRole } from '../../utils/userRoles';
import { ArchitectRoleBadge } from '../pm/ArchitectRoleBadge';
import '../pm/ArchitectRoleBadge.css';
import UserAvatar, { UserAvatarProps } from './UserAvatar';
import { CRMUser } from '../../types';
import { useCRM } from '../../utils/CRMContext';
import {
  formatUserLocalTimeWithOffset,
  getViewerTimezone,
  isValidIanaTimezone,
} from '../../utils/userTimezone';
import './UserHoverCard.css';

export interface UserHoverCardProps extends Omit<UserAvatarProps, 'title'> {
  userId?: string;
  crmUser?: CRMUser;
}

const TICK_MS = 30_000;

const UserHoverCard: React.FC<UserHoverCardProps> = ({
  userId,
  name,
  photoURL,
  className,
  style,
  crmUser: crmUserProp,
}) => {
  const { crmUsers } = useCRM();
  const anchorRef = useRef<HTMLSpanElement>(null);
  const popoverRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const [now, setNow] = useState(() => new Date());

  const crmUser = useMemo(() => {
    if (crmUserProp) return crmUserProp;
    if (!userId) return undefined;
    return crmUsers.find((u) => u.uid === userId);
  }, [crmUserProp, userId, crmUsers]);

  const profile = useMemo(() => {
    const email = crmUser?.email?.trim() || '';
    const phone = crmUser?.phone?.trim() || '';
    const location = crmUser?.location?.trim() || '';
    const timezone = crmUser?.timezone?.trim() || '';
    const hasDetails = Boolean(email || phone || location || (timezone && isValidIanaTimezone(timezone)));
    return { email, phone, location, timezone, hasDetails };
  }, [crmUser]);

  const viewerTz = useMemo(() => getViewerTimezone(), []);
  const timeLine = useMemo(() => {
    if (!profile.timezone || !isValidIanaTimezone(profile.timezone)) return '';
    return formatUserLocalTimeWithOffset(profile.timezone, viewerTz, now);
  }, [profile.timezone, viewerTz, now]);

  const reposition = useCallback(() => {
    const anchor = anchorRef.current;
    const pop = popoverRef.current;
    if (!anchor || !pop) return;
    const r = anchor.getBoundingClientRect();
    const pw = Math.max(pop.offsetWidth, 1);
    const ph = Math.max(pop.offsetHeight, 1);
    const margin = 8;
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let left = r.left + r.width / 2 - pw / 2;
    left = Math.max(margin, Math.min(left, vw - pw - margin));
    let top = r.top - ph - margin;
    if (top < margin) top = r.bottom + margin;
    if (top + ph > vh - margin) top = Math.max(margin, vh - ph - margin);
    setPos((prev) => (prev && prev.top === top && prev.left === left ? prev : { top, left }));
  }, []);

  useLayoutEffect(() => {
    if (!open) {
      setPos(null);
      return;
    }
    reposition();
    const id = window.requestAnimationFrame(() => reposition());
    return () => window.cancelAnimationFrame(id);
  }, [open, reposition, profile, timeLine]);

  useEffect(() => {
    if (!open) return;
    const onScrollOrResize = () => reposition();
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open, reposition]);

  useEffect(() => {
    if (!open) return;
    setNow(new Date());
    const id = window.setInterval(() => setNow(new Date()), TICK_MS);
    return () => window.clearInterval(id);
  }, [open]);

  const popoverBody = (
    <div className="uhc-card">
      <div className="uhc-name-row">
        <div className="uhc-name">{crmUser?.displayName || name}</div>
        {isArchitectRole(crmUser?.role) && <ArchitectRoleBadge compact />}
      </div>
      {isArchitectRole(crmUser?.role) && (
        <p className="uhc-role">
          {crmUser?.company?.trim() ? `${crmUser.company.trim()} · Project architect` : 'Project architect'}
        </p>
      )}
      {!profile.hasDetails ? (
        <p className="uhc-empty">No profile details</p>
      ) : (
        <ul className="uhc-rows">
          {profile.location && (
            <li className="uhc-row">
              <MapPin size={13} aria-hidden className="uhc-icon" />
              <span>{profile.location}</span>
            </li>
          )}
          {profile.email && (
            <li className="uhc-row">
              <Mail size={13} aria-hidden className="uhc-icon" />
              <a href={`mailto:${profile.email}`}>{profile.email}</a>
            </li>
          )}
          {profile.phone && (
            <li className="uhc-row">
              <Phone size={13} aria-hidden className="uhc-icon" />
              <a href={`tel:${profile.phone.replace(/\s/g, '')}`}>{profile.phone}</a>
            </li>
          )}
          {timeLine && (
            <li className="uhc-row uhc-row--time">
              <Clock size={13} aria-hidden className="uhc-icon" />
              <span>{timeLine}</span>
            </li>
          )}
        </ul>
      )}
    </div>
  );

  return (
    <>
      <span
        ref={anchorRef}
        className="uhc-anchor"
        tabIndex={0}
        aria-label={`${name} profile`}
        onMouseEnter={() => setOpen(true)}
        onMouseLeave={() => setOpen(false)}
        onFocus={() => setOpen(true)}
        onBlur={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOpen(false);
        }}
      >
        <UserAvatar
          name={name}
          photoURL={photoURL ?? crmUser?.photoURL}
          userId={userId}
          className={className}
          style={style}
        />
      </span>
      {open &&
        createPortal(
          <div
            ref={popoverRef}
            className="uhc-popover"
            style={
              pos
                ? { top: pos.top, left: pos.left }
                : { top: -9999, left: 0, visibility: 'hidden' as const }
            }
            role="tooltip"
            onMouseEnter={() => setOpen(true)}
            onMouseLeave={() => setOpen(false)}
          >
            {popoverBody}
          </div>,
          document.body,
        )}
    </>
  );
};

export default UserHoverCard;
